import { CAMPAIGN_VARIABLES } from '@crm/shared';
import { Input, Label, Modal, type ModalAction, Textarea } from '@crm/ui';
import { Send } from 'lucide-react';
import { useRef, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { type MessageTemplate, useRequestApproval, useSaveTemplate } from '@/modules/messages/api';
import { TemplateStatus } from './template-status';
import { WhatsappBubble } from './whatsapp-bubble';

/**
 * O modelo da campanha, escrito de dentro da propria campanha: quem esta
 * montando o disparo nao deveria sair da tela para escrever a frase que vai
 * disparar.
 *
 * A situacao nao se escolhe - quem responde e a Meta. Daqui so sai o pedido.
 */
export function TemplateModal({
  template,
  sugestaoDeNome,
  coupon,
  onClose,
  onSaved,
}: {
  /** Nulo = criando um modelo novo. */
  template: MessageTemplate | null;
  sugestaoDeNome: string;
  coupon?: { code: string; type: string; value: number } | null;
  onClose: () => void;
  onSaved: (template: MessageTemplate) => void;
}) {
  const save = useSaveTemplate();
  const aprovacao = useRequestApproval();
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [name, setName] = useState(template?.name ?? sugestaoDeNome);
  const [body, setBody] = useState(template?.body ?? '');

  /** A marcacao entra por botao: digitada na mao, "{{clientes}}" vira texto. */
  function inserir(chave: string) {
    const campo = bodyRef.current;
    const marca = `{{${chave}}}`;

    if (!campo) {
      setBody((atual) => atual + marca);
      return;
    }

    const inicio = campo.selectionStart ?? body.length;
    const fim = campo.selectionEnd ?? body.length;

    setBody(`${body.slice(0, inicio)}${marca}${body.slice(fim)}`);
    requestAnimationFrame(() => {
      campo.focus();
      campo.setSelectionRange(inicio + marca.length, inicio + marca.length);
    });
  }

  /**
   * Grava o texto e, quando pedido, manda para a Meta. Nesta ordem: o que vai
   * para analise e o que esta salvo, nunca o rascunho da tela.
   */
  function salva(pedirAprovacao: boolean) {
    save.mutate(
      { id: template?.id, input: { name, event: 'CAMPAIGN', body, isActive: true } },
      {
        onSuccess: (salvo) => {
          if (!pedirAprovacao) {
            onSaved(salvo);
            return;
          }

          aprovacao.mutate(salvo.id, { onSuccess: onSaved });
        },
      },
    );
  }

  const completo = name.trim().length > 0 && body.trim().length > 0;
  const ocupado = save.isPending || aprovacao.isPending;
  // Aprovado nao se pede de novo, e em analise ja esta pedido.
  const podePedir = !template || template.approval === 'DRAFT' || template.approval === 'REJECTED';
  const erro = save.error ?? aprovacao.error;

  const acoes: ModalAction[] = [
    { label: 'Cancelar', variant: 'outline', onClick: onClose },
    {
      label: 'Salvar',
      variant: podePedir ? 'outline' : 'default',
      disabled: !completo,
      loading: ocupado,
      onClick: () => salva(false),
    },
  ];

  if (podePedir) {
    acoes.push({
      label: 'Solicitar aprovacao',
      icon: <Send />,
      disabled: !completo,
      loading: ocupado,
      onClick: () => salva(true),
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={template ? 'Editar modelo' : 'Novo modelo'}
      subtitle="O texto que a Meta precisa aprovar antes do disparo"
      size="medium"
      bodyClassName="bg-background"
      actions={acoes}
    >
      <div className="space-y-4">
        {erro ? <p className="text-sm text-destructive">{toErrorMessage(erro)}</p> : null}

        <div className="space-y-1.5">
          <Label htmlFor="campaign-template-name">Nome do modelo</Label>
          <Input
            id="campaign-template-name"
            value={name}
            onChange={(input) => setName(input.target.value)}
            placeholder="Promocao com cupom"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="campaign-template-body">Mensagem</Label>
          <Textarea
            id="campaign-template-body"
            ref={bodyRef}
            rows={5}
            value={body}
            onChange={(input) => setBody(input.target.value)}
            placeholder="Oi {{cliente}}, temos novidade na {{unidade}}!"
          />

          <div className="flex flex-wrap gap-1.5 pt-1">
            {CAMPAIGN_VARIABLES.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => inserir(item.key)}
                className="rounded-full border border-border-strong bg-card px-2.5 py-1 text-xs text-slate-600 transition-colors hover:bg-muted dark:text-slate-300"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {body ? (
          <div className="space-y-1.5">
            <Label>Como chega no WhatsApp</Label>
            <WhatsappBubble body={body} coupon={coupon} />
          </div>
        ) : null}

        {/* Quem responde e a Meta: aqui so se ve em que pe esta o pedido. */}
        <div className="space-y-2">
          <Label>Aprovacao da Meta</Label>
          <TemplateStatus template={template} />
        </div>
      </div>
    </Modal>
  );
}
